import { BinaryReader } from 'Loader/serialization/common/BinaryReader';
import { GeometryChunkId } from './GeometryChunkId';

export type GeomHeaderSchema = {
    vMajor: number;
    vMinor: number;
    version: number;
    timestamp: bigint;
};

export type GeomChunkMeta = {
    chunkId: number;
    chunkSize: number;
};

abstract class GeomSerializable<PreviousSchema, LatestSchema> {
    protected abstract readonly expectedChunkId: GeometryChunkId;
    protected abstract migrateToLatest(anyPrevious: PreviousSchema): LatestSchema;
    protected abstract deserializeContent(header: GeomHeaderSchema, reader: BinaryReader): LatestSchema;

    public deserialize(data: ArrayBuffer): LatestSchema {
        const reader = new BinaryReader(data);
        const header = this.readHeader(reader);
        const chunkMeta = this.readChunkMeta(reader);
        this.validateChunkMeta(chunkMeta);
        this.validateChunkSize(chunkMeta, reader);
        return this.deserializeContent(header, reader);
    }

    private readHeader(reader: BinaryReader): GeomHeaderSchema {
        const vMajor = reader.readUInt32();
        const vMinor = reader.readUInt32();
        const version = reader.readUInt32();
        const timestamp = reader.readUInt64() as bigint;
        return { vMajor, vMinor, version, timestamp };
    }

    private readChunkMeta(reader: BinaryReader): GeomChunkMeta {
        const chunkId = reader.readUInt8();
        const chunkSize = reader.readUInt32();
        return { chunkId, chunkSize };
    }

    private validateChunkMeta(chunkMeta: GeomChunkMeta) {
        if (chunkMeta.chunkId !== this.expectedChunkId) {
            throw new Error(`Unexpected geometry chunk ${chunkMeta.chunkId}; expected ${this.expectedChunkId}.`);
        }
    }

    private validateChunkSize(chunkMeta: GeomChunkMeta, reader: BinaryReader) {
        const remaining = reader.getData().byteLength - reader.getPos();
        if (chunkMeta.chunkSize !== remaining) {
            console.warn(
                `[GeomSerializable] Chunk size mismatch for chunk ${chunkMeta.chunkId}: header=${chunkMeta.chunkSize}, remaining=${remaining}`
            );
        }
    }
}

export default GeomSerializable;
